import * as z from 'zod';

// ---------------------------------------------------------------------------
// Primitive enums
// ---------------------------------------------------------------------------

export const GameSizeSchema = z.union([z.literal(1000), z.literal(2000)]);
export type GameSize = z.infer<typeof GameSizeSchema>;

export const RoundModeSchema = z.enum(['five-round', 'infinite']);
export type RoundMode = z.infer<typeof RoundModeSchema>;

export const SideSchema = z.enum(['horde', 'players']);
export type Side = z.infer<typeof SideSchema>;

export const HordePhaseSchema = z.enum([
    'start-of-round',
    'horde-command',
    'horde-movement',
    'horde-shooting',
    'horde-charge',
    'horde-fight',
    'player-turn',
    'end-of-round',
]);
export type HordePhase = z.infer<typeof HordePhaseSchema>;

/**
 * Canonical round walkthrough order. `horde-command` is the legacy key for the players' Command/Resupply step.
 */
export const HORDE_PHASE_ORDER: readonly HordePhase[] = [
    'start-of-round',
    'horde-movement',
    'horde-shooting',
    'horde-charge',
    'horde-fight',
    'horde-command',
    'player-turn',
    'end-of-round',
] as const;

// ---------------------------------------------------------------------------
// Players (contracts §4 amendment)
// ---------------------------------------------------------------------------

export const PlayerLiteSchema = z.object({
    cp: z.number(),
    faction: z.string(),
    id: z.string(),
    name: z.string(),
    pointsShare: z.number(),
    /**
     * Resupply entry ids this player has purchased in the current battle round - drives the
     * once-per-turn(-per-player) purchase limits. Reset every START_ROUND. Optional so older persisted snapshots
     * and test fixtures load unchanged; the reducer treats a missing value as `[]`.
     */
    purchasesThisRound: z.array(z.string()).optional(),
    /**
     * Resupply cards purchased/held for later use (contracts §4).
     */
    savedCards: z.array(z.string()),
    secretObjectiveId: z.string().nullable(),
    sp: z.number(),
    /**
     * Supply Points this player has *gained* so far in the current battle round, from every source (objective
     * award, kill attribution, card effects). Spending never decrements it - Hard Mode caps gains per round
     * (REQ-HARDMODE-2), not net balance - and it resets to `0` on every `START_ROUND` alongside
     * `purchasesThisRound`. Tallied unconditionally rather than only under Hard Mode, so toggling the rule
     * mid-game reads a truthful running total instead of starting from a blank slate.
     *
     * Defaults to `0` so games persisted before this field existed parse unchanged (no `SCHEMA_VERSION` bump).
     */
    spGainedThisRound: z.number().default(0),
    warlordPresent: z.boolean(),
});
export type PlayerLite = z.infer<typeof PlayerLiteSchema>;

// ---------------------------------------------------------------------------
// Cards (contracts §4 prototype CardsState)
// ---------------------------------------------------------------------------

export const CardDeckIdSchema = z.enum(['misery', 'secondary', 'secret-objective']);
export type CardDeckId = z.infer<typeof CardDeckIdSchema>;

// ---------------------------------------------------------------------------
// Spawn modifier stack (contracts §4 - additive only in prototype)
// ---------------------------------------------------------------------------

export const SpawnModifierSchema = z.object({
    expiresAfterRound: z.number().optional(),
    id: z.string(),
    /**
     * `additive` stacks a +/- value onto the roll; `full-replacement` forces the roll total to `value` outright
     * (Adepta Sororitas "Martyrs and Miracles": treat the first Spawn Roll each round as an unmodified 12 from
     * round 4).
     */
    kind: z.enum(['additive', 'full-replacement']),
    scope: z.enum(['all-zones', 'first-zone']),
    source: z.string(),
    /**
     * Lower-bound round the modifier becomes active (Sororitas `fromRound: 4`).
     */
    startsAtRound: z.number().optional(),
    value: z.number(),
});
export type SpawnModifier = z.infer<typeof SpawnModifierSchema>;
